import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { ApiService } from '../../../shared/services/api.service';
import { take } from 'rxjs';

interface LogColumn {
  id: string;
  label: string;
  selected: boolean;
}

@Component({
  selector: 'app-log-exporter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatCheckboxModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
  ],
  templateUrl: './log-exporter.component.html',
  styleUrls: ['./log-exporter.component.scss'],
})
export class LogExporterComponent {
  private apiService = inject(ApiService);

  isExporting = signal(false);
  startDate = new Date(new Date().setDate(new Date().getDate() - 30));
  endDate = new Date();

  columns = signal<LogColumn[]>([
    { id: 'pk', label: 'Partition Key', selected: false },
    { id: 'sk', label: 'Sort Key', selected: false },
    { id: 'messageHistory', label: 'Message History', selected: true },
    { id: 'planNumber', label: 'Plan Number', selected: false },
    { id: 'productType', label: 'Product Type', selected: true },
    { id: 'summary', label: 'AI Summary', selected: true },
    { id: 'feedbackContent', label: 'User Feedback', selected: true },
    { id: 'rating', label: 'Rating', selected: false },
    { id: 'completedAt', label: 'Completed At', selected: true },
  ]);

  exportData(format: 'csv' | 'html') {
    this.isExporting.set(true);

    const selectedCols = this.columns()
      .filter((c) => c.selected)
      .map((c) => c.id);

    const payload = {
      startDate: this.startDate.toISOString().split('T')[0],
      endDate: this.endDate.toISOString().split('T')[0],
      columns: selectedCols, // Note: Backend must support column filtering or we filter client-side
    };

    this.apiService
      .postCsv<any, any>('admin/export-bedrock-conversation-logs', payload)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (format === 'csv') {
            this.downloadFile(response, 'text/csv', 'logs.csv');
          } else {
            this.generateHtmlReport(response);
          }
          this.isExporting.set(false);
        },
        error: (err) => {
          console.error('Export failed:', err);
          this.isExporting.set(false);
          alert('Export failed. Please try again.');
        },
      });
  }

  private downloadFile(content: string, type: string, filename: string) {
    const blob = new Blob([content], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  private generateHtmlReport(csvContent: string) {
    // Basic CSV to JSON conversion for the report
    const lines = csvContent.split('\n');
    const headers = lines[0].split(',').map((h) => h.replace(/"/g, ''));
    const rows = lines.slice(1).filter((l) => l.trim() !== '');

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Log Report</title>
        <style>
          body { font-family: sans-serif; padding: 40px; background: #f0f2f5; }
          .card { background: white; padding: 20px; margin-bottom: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); border-left: 6px solid #007bff; }
          .header { font-weight: bold; color: #007bff; text-transform: uppercase; font-size: 12px; margin-top: 10px; }
          .content { white-space: pre-wrap; margin-bottom: 10px; }
        </style>
      </head>
      <body>
        <h1>Bedrock Conversation Report</h1>
    `;

    rows.forEach((row) => {
      // Rough CSV parsing - in real app use a library like papaparse
      const values = row
        .split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/)
        .map((v) => v.replace(/"/g, ''));

      html += '<div class="card">';
      headers.forEach((header, idx) => {
        // Only show if the column was selected
        const colDef = this.columns().find(
          (c) => c.label === header || c.id === header
        );
        if (colDef?.selected && values[idx]) {
          html += `<div class="header">${header}</div>`;
          html += `<div class="content">${values[idx]}</div>`;
        }
      });
      html += '</div>';
    });

    html += '</body></html>';
    this.downloadFile(html, 'text/html', 'report.html');
  }
}
